/**
 * Bank reconciliation (ARCHITECTURE §3.1). Pure functions that take the parsed
 * lines of a bank statement (the "cash ledger") and match them against the bank
 * movements already recorded in the books, so a user can see at a glance what is
 * cleared, what is on the statement but not yet booked, and what is booked but
 * not yet on the statement. Nothing here is stored — it derives from the ledger
 * and the uploaded file, so it can never drift from the source of truth.
 *
 * Sign convention: an amount is positive when money moves INTO the bank account
 * (a deposit / a debit to the bank asset) and negative when money moves OUT.
 */
import type { Currency, JournalEntry, Money } from "./types";
import { add, isZero, subtract, zero } from "./money";

/** One line from an uploaded bank statement, normalized. */
export interface BankTxn {
  readonly id: string;
  /** ISO date, YYYY-MM-DD. */
  readonly date: string;
  readonly description: string;
  /** Signed: positive = money in, negative = money out. */
  readonly amount: Money;
}

/** A net movement on a bank/cash account recorded in the books. */
export interface BookMovement {
  readonly entryId: string;
  readonly ref: string;
  readonly date: string;
  readonly description: string;
  readonly accountCode: string;
  /** Signed net effect on the bank account: positive = into bank (debit). */
  readonly amount: Money;
}

/** A bank line paired with the book movement it clears. */
export interface Match {
  readonly bank: BankTxn;
  readonly book: BookMovement;
  /** Absolute difference in days between the two dates. */
  readonly dayGap: number;
}

export interface Reconciliation {
  readonly matched: readonly Match[];
  /** On the statement but not in the books — candidates to record. */
  readonly unmatchedBank: readonly BankTxn[];
  /** In the books but not on the statement — uncleared / outstanding. */
  readonly unmatchedBook: readonly BookMovement[];
  /** Net of every line on the statement. */
  readonly statementTotal: Money;
  /** Net of the matched (cleared) lines. */
  readonly clearedTotal: Money;
  /** True when every statement line and every book movement is matched. */
  readonly reconciled: boolean;
}

export interface ReconcileOptions {
  /** Max date distance (days) still allowed to match. Default 5. */
  readonly windowDays?: number;
}

/**
 * Extract the net bank movement per (entry, bank account) from posted entries.
 * `bankCodes` is the set of chart codes treated as bank/cash accounts.
 */
export function bankMovements(
  entries: readonly JournalEntry[],
  bankCodes: ReadonlySet<string>,
  base: Currency,
  entityId?: string,
): BookMovement[] {
  const posted = entries.filter(
    (e) => e.status === "posted" && (entityId === undefined || e.entityId === entityId),
  );
  const out: BookMovement[] = [];
  for (const e of posted) {
    const perAccount = new Map<string, Money>();
    for (const l of e.lines) {
      if (!bankCodes.has(l.accountCode)) continue;
      const net = subtract(l.debit, l.credit); // positive = into bank
      perAccount.set(l.accountCode, add(perAccount.get(l.accountCode) ?? zero(base), net));
    }
    for (const [accountCode, amount] of perAccount) {
      if (isZero(amount)) continue;
      out.push({
        entryId: e.id,
        ref: e.ref,
        date: e.date,
        description: e.description,
        accountCode,
        amount,
      });
    }
  }
  return out;
}

/** Absolute whole-day gap between two ISO (YYYY-MM-DD) dates. */
function dayGap(a: string, b: string): number {
  const ms = Math.abs(Date.parse(a) - Date.parse(b));
  return Number.isFinite(ms) ? Math.round(ms / 86_400_000) : Number.POSITIVE_INFINITY;
}

/**
 * Greedily match each bank line to an unused book movement with the same signed
 * amount and currency, within the date window, preferring the closest date.
 * Deterministic: bank lines are matched in date order.
 */
export function reconcile(
  bank: readonly BankTxn[],
  book: readonly BookMovement[],
  base: Currency,
  options: ReconcileOptions = {},
): Reconciliation {
  const windowDays = options.windowDays ?? 5;
  const used = new Set<number>();
  const matched: Match[] = [];
  const unmatchedBank: BankTxn[] = [];

  const bankSorted = [...bank].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );

  for (const bt of bankSorted) {
    let bestIdx = -1;
    let bestBook: BookMovement | undefined;
    let bestGap = Number.POSITIVE_INFINITY;
    for (let i = 0; i < book.length; i += 1) {
      if (used.has(i)) continue;
      const bm = book[i];
      if (!bm) continue;
      if (bm.amount.currency !== bt.amount.currency) continue;
      if (bm.amount.amount !== bt.amount.amount) continue;
      const gap = dayGap(bt.date, bm.date);
      if (gap > windowDays) continue;
      if (gap < bestGap) {
        bestGap = gap;
        bestIdx = i;
        bestBook = bm;
      }
    }
    if (bestBook) {
      used.add(bestIdx);
      matched.push({ bank: bt, book: bestBook, dayGap: bestGap });
    } else {
      unmatchedBank.push(bt);
    }
  }

  const unmatchedBook = book.filter((_, i) => !used.has(i));
  const statementTotal = bank.reduce((s, t) => add(s, t.amount), zero(base));
  const clearedTotal = matched.reduce((s, m) => add(s, m.bank.amount), zero(base));

  return {
    matched,
    unmatchedBank,
    unmatchedBook,
    statementTotal,
    clearedTotal,
    reconciled: unmatchedBank.length === 0 && unmatchedBook.length === 0,
  };
}
